/**
 * Fire an `empty_state_shown` event once per mount when a surface settles
 * into an empty state.
 *
 * This is the silent-breakage detector layer: bug-5d ("no signals" on one
 * surface) and #18 (blank AI summaries) were invisible on prod because empty
 * states emitted nothing. Note that "empty" is NOT always "broken" — callers
 * should pass discriminating properties (has_active_filters, account counts)
 * so dashboards can separate expected-empty from broken-empty; alerting is
 * scoped to surfaces where empty ≈ broken on a mature account.
 *
 * Fires at most once per mount: the guard ref survives re-renders (and the
 * StrictMode dev double-render), and a surface that loads non-empty then
 * empties through user filtering re-fires only on remount.
 */
import { useEffect, useRef } from "react";
import { useAnalytics } from "./useAnalytics";

export const useEmptyStateTracking = (
  surface,
  { isEmpty, isLoading, properties = {} },
) => {
  const { trackEvent } = useAnalytics();
  const firedRef = useRef(false);
  const propsRef = useRef(properties);
  propsRef.current = properties;

  useEffect(() => {
    if (isLoading || !isEmpty || firedRef.current) return;
    firedRef.current = true;
    trackEvent("empty_state_shown", { surface, ...propsRef.current });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmpty, isLoading, surface]);
};
